import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sun, Sunset, Moon, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, todayStr, errMsg } from "@/lib/api";

const SHIFT_OPTS = [
  { name: "Morning", icon: Sun },
  { name: "Afternoon", icon: Sunset },
  { name: "Night", icon: Moon },
];

export default function StartPage() {
  const nav = useNavigate();
  const [name, setName] = useState(localStorage.getItem("fda_name") || "");
  const [date, setDate] = useState(todayStr());
  const [shift, setShift] = useState("Morning");
  const [loading, setLoading] = useState(false);

  const start = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your name");
    setLoading(true);
    try {
      const { data } = await api.post("/checklists", { staff_name: name.trim(), date, shift });
      localStorage.setItem("fda_name", name.trim());
      nav(`/checklist/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
      <div className="rise">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C87D55] mb-4">Daily Task · Front Desk Agent</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#2A3C36] leading-[1.05]">
          Start your shift <span className="font-accent italic font-medium text-[#C87D55]">checklist</span>
        </h1>
        <p className="mt-6 text-sm sm:text-base text-[#666059] max-w-md leading-relaxed">
          Enter your name, pick the date and shift. Your progress is saved so you can continue anytime, from any device.
        </p>
        <img
          src="/hotel.webp"
          alt="Sikara Lombok Hotel"
          className="hidden lg:block mt-10 w-full h-56 object-cover rounded-2xl"
        />
      </div>

      <form onSubmit={start} className="rise bg-white border border-[#E2DAD0] rounded-2xl p-6 sm:p-8 shadow-[0_2px_8px_rgba(42,60,54,0.04)] space-y-6" style={{ animationDelay: ".1s" }} data-testid="start-form">
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-[#666059]">FDA Name</label>
          <Input data-testid="start-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-12 bg-[#FBF9F5]" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-[#666059]">Date</label>
          <Input data-testid="start-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-12 bg-[#FBF9F5]" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-[#666059]">Shift</label>
          <div className="grid grid-cols-3 gap-3">
            {SHIFT_OPTS.map(({ name: s, icon: Icon }) => (
              <button
                type="button"
                key={s}
                data-testid={`start-shift-${s.toLowerCase()}`}
                onClick={() => setShift(s)}
                className={`flex flex-col items-center gap-2 py-4 rounded-xl border text-sm font-medium transition-colors duration-200 ${
                  shift === s ? "border-[#C87D55] bg-[#C87D55]/10 text-[#A85832]" : "border-[#E2DAD0] text-[#666059] hover:bg-[#F4F0EA]"
                }`}
              >
                <Icon className="w-5 h-5" />
                {s}
              </button>
            ))}
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="start-submit-button" className="w-full h-12 rounded-full text-base bg-[#C87D55] hover:bg-[#A85832]">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Open Checklist <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </form>
    </div>
  );
}
